import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, X } from 'lucide-react';
import { uploadInvoiceFiles } from '../../invoices/api';
import { uploadFromPagesResponse, uploadsFromResponse } from '../../invoices/InvoiceUploader';
import { rememberUploads } from '../../invoices/recentUploads';
import CameraCapture from '../../capture/CameraCapture';

const OFFLINE_MESSAGE = 'You are offline. The pages were not sent; they are kept here, so try again when you are back online.';

/**
 * One tap from Home: scan page 1, 2, 3 ... of a supplier invoice → "Send as one invoice (N pages)" → the Invoices
 * screen shows it being read. Reuses the desktop's multi-page scanner (capture/CameraCapture.js, `onSendPages`), so
 * the pages can be put in order, retaken or deleted before sending, and goes through the same upload as the desktop
 * (`uploadInvoiceFiles(files, { asOneDocument: true })`): N pages make ONE document, not N invoices.
 */
export default function QuickInvoicePhoto({ to = '/mobile/invoices' }) {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);

  // A refusal is thrown back to the scanner, which keeps the pages on screen to try again.
  const sendPages = async (blobs) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new Error(OFFLINE_MESSAGE);
    const startedAt = Date.now();
    const files = blobs.map((b, i) => new File([b], `scan-page-${i + 1}.jpg`, { type: b.type || 'image/jpeg' }));
    const body = await uploadInvoiceFiles(files, { asOneDocument: true });
    // One page is just an invoice (the server only joins two or more); say so rather than "1 pages".
    rememberUploads(files.length === 1
      ? uploadsFromResponse(files, body, startedAt)
      : uploadFromPagesResponse(files, body, startedAt));
    setScanning(false);
    navigate(to);
  };

  return (
    <div className="px-4" data-testid="quick-invoice-photo">
      {!scanning ? (
        <button type="button" onClick={() => setScanning(true)} data-testid="quick-invoice-photo-btn"
          className="w-full flex items-center gap-4 min-h-[72px] px-5 rounded-2xl bg-accent-500 text-white shadow-ambient active:scale-[0.99] motion-organic">
          <Camera className="w-7 h-7" />
          <span className="text-left">
            <span className="block font-display text-lg leading-tight">Scan invoice</span>
            <span className="block text-sm opacity-90">One page or several, sent as one invoice and read for you</span>
          </span>
        </button>
      ) : (
        <div className="rounded-2xl bg-surface border border-surface-border shadow-ambient p-4 space-y-3" data-testid="quick-invoice-scan">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm text-ink">
              Take page 1, then page 2 and so on. Put them in order, retake any that are blurred, then send them as one invoice.
            </p>
            <button type="button" onClick={() => setScanning(false)} data-testid="quick-invoice-scan-cancel"
              aria-label="Cancel scanning" className="shrink-0 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-ink-muted hover:bg-surface-sunken">
              <X className="w-5 h-5" />
            </button>
          </div>
          <CameraCapture captureType="supplier_invoice" onSendPages={sendPages} />
        </div>
      )}
    </div>
  );
}
